import { Injectable } from "@angular/core";
import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { ServiceFormatter } from "../../helpers/service-formatter";
import { FileDataTableValuesRequest } from "../../models/file-data-table-values-request";
import { FileDownloadRequest } from "../../models/file-download-request";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { AsmFilesGridResponse } from "../../models/asm-files-grid-response";
import { MyExportsGridResponse } from "../../models/my-exports-grid-response";
import { MyExportsRequest } from "../../models/my-exports-request";

@Injectable({
  providedIn: 'root'
})
export class FileDataService {
  private endpoint = ServiceFormatter.endpoint;

  constructor(private http: HttpClient) { }

  getFileDataTableItems(query: FileDataTableValuesRequest) {
    const params: HttpParams = new HttpParams()
      .set("payerIds", query.payerIds)
      .set("fileTypeIds", query.fileTypeIds.join(","))
      .set("thresholdDateString", query.thresholdDateString);
    return this.http.get<any>(this.endpoint + "FileData/file-data-table-items", ServiceFormatter.getHttpOptions(params));
  }

  uploadFile(fileForm: FormData) {
    const standardHttpOptions = ServiceFormatter.getHttpOptions();
    return this.http.post<any>(this.endpoint + "FileData/file-upload", fileForm, {
      headers: standardHttpOptions.headers,
      params: standardHttpOptions.params,
      observe: 'events',
      reportProgress: true
    });
  }

  getAsmFilesGridData(payerIds: string, thresholdDateString: string) {
    const params: HttpParams = new HttpParams()
      .set("payerIds", payerIds)
      .set("thresholdDateString", thresholdDateString);
    return this.http.get<AsmFilesGridResponse>(this.endpoint + "FileData/asm-files-grid-items", ServiceFormatter.getHttpOptions(params));
  }

  downloadFile(request: FileDownloadRequest): Observable<HttpResponse<Blob>> {
    return this.http.post(this.endpoint + "FileData/download", request, {
      ...ServiceFormatter.getHttpOptions(),
      observe: 'response',
      responseType: 'blob'
    }).pipe(
      tap(res => {
        this.triggerBrowserDownload(res);
      })
    );
  }

  getMyExports(thresholdDateString?: string, exportTypeIds?: number[]) {
    const request: MyExportsRequest = {
      thresholdDateString,
      exportTypeIds
    };
    return this.http.post<MyExportsGridResponse>(this.endpoint + "FileData/my-exports", request, ServiceFormatter.getHttpOptions());
  }

  private triggerBrowserDownload(response: HttpResponse<Blob>) {
    const blob = response.body;
    if (!blob) { return; }
    
    const filename = this.extractFilenameFromContentDisposition(response.headers.get('content-disposition')) ?? 'downloaded_file';

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private extractFilenameFromContentDisposition(header: string | null): string | null {
    if (!header) { return null; }
    let match = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
    if (match && match[1]) {
      try {
        return decodeURIComponent(match[1].trim().replace(/['"]/g, ''));
      }
      catch {
        // fall through
      }
    }

    match = /filename\s*=\s*"?([^\";]+)"?/i.exec(header);
    if (match && match[1]) {
      return match[1].trim();
    }

    return null;
  }
}